import { Button, Typography, type ButtonProps, type SxProps, type Theme } from '@mui/material';
import { styled } from '@mui/material/styles';

import useBrainSession from '../../../../../../services/BrainSession/useBrainSession.ts';

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'kind',
})<{ kind?: 'default' | 'transparent' | 'light' | 'blank' | 'blankWhite' }>(
  ({ theme, kind = 'default' }) => ({
    minWidth: 156,

    ...(kind === 'blank' && {
      color: '#031839D9',
      border: `2px solid transparent`,
      backgroundColor: 'transparent',
      '&:hover': {
        color: '#000',
        backgroundColor: 'transparent',
        border: `2px solid transparent`,
        textDecoration: 'underline',
      },
    }),

    ...(kind === 'blankWhite' && {
      color: theme.palette.common.white,
      border: `2px solid transparent`,
      backgroundColor: 'transparent',
      '&:hover': {
        color: theme.palette.common.white,
        backgroundColor: 'transparent',
        border: `2px solid transparent`,
        textDecoration: 'underline',
      },
    }),

    ...(kind === 'transparent' && {
      color: theme.palette.chat.button.transparent.text,
      border: `2px solid ${theme.palette.chat.button.transparent.border}`,
      backgroundColor: 'transparent',
      '&:hover': {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.chat.button.transparent.border,
        textDecoration: 'none',
      },
    }),

    ...(kind === 'light' && {
      color: theme.palette.chat.button.transparent.text,
      border: `2px solid ${theme.palette.primary.light}`,
      backgroundColor: 'transparent',
      '&:hover': {
        border: `2px solid ${theme.palette.primary.light}`,
        color: theme.palette.chat.button.transparent.text,
        backgroundColor: theme.palette.primary.light,
        textDecoration: 'none',
      },
    }),
  }),
);

const ButtonLabel = styled(Typography)(() => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})) as typeof Typography;

type ChatButtonProps = {
  children?: React.ReactNode;
  label: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'transparent' | 'light' | 'blank' | 'blankWhite';
  className?: string;
  buttonId?: string | null;
  size?: 'small' | 'medium' | 'large';
  action?: string | null;
  labelClassName?: string;
  sx?: SxProps<Theme>;
  fullWidth?: boolean;
} & Omit<ButtonProps, 'variant' | 'action'>;

const ChatButton: React.FC<ChatButtonProps> = ({
  children = null,
  label,
  variant = 'default',
  onClick,
  className = '',
  buttonId = null,
  size = 'large',
  action = null,
  labelClassName = '',
  ...props
}) => {
  const { closeChatModal } = useBrainSession();

  return (
    <StyledButton
      kind={variant}
      className={className}
      size={size}
      variant="contained"
      color={variant === 'default' ? 'secondary' : 'inherit'}
      onClick={action === 'close' ? (closeChatModal as any) : onClick}
      aria-label={buttonId || (label as string)}
      id={buttonId || (label as string)}
      {...props}
    >
      <ButtonLabel component="div" variant="button2n" className={labelClassName}>
        {label} {children}
      </ButtonLabel>
    </StyledButton>
  );
};

export type { ChatButtonProps };
export default ChatButton;
